import { Link } from "react-router-dom";
import { Droplets, Lightbulb, Sparkles, MapPin, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const features = [
  { icon: Droplets, title: "Pressure Washing", desc: "$2 per sqm for driveways, paths and patios." },
  { icon: Lightbulb, title: "LED Pathway Lighting", desc: "Continuous strips that transform the way your home glows at dusk." },
  { icon: Sparkles, title: "Combined Package", desc: "Wash and light in one visit - one team, one tidy invoice." },
];

export default function Home() {
  useDocumentMeta({
    title: "Shine and Line Services | Pressure Washing & LED Lighting in Rangiora",
    description: "Local Rangiora team offering $3/sqm pressure washing for driveways and paths, plus continuous LED pathway lighting. Free quotes, fast bookings across North Canterbury.",
  });
  return (
    <div className="overflow-hidden">
      {/* HERO - DARK */}
      <section className="relative bg-[#0A0A0B] text-white overflow-hidden" data-testid="hero-section">
        {/* Decorative blue glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-40 w-[600px] h-[600px] rounded-full bg-[#0EA5E9]/20 blur-3xl" />
          <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] rounded-full bg-[#0369A1]/30 blur-3xl" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 sm:pt-16 lg:pt-20 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 reveal" style={{ animationDelay: "60ms" }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur text-[#7DD3FC] px-3 py-1 text-xs font-medium tracking-wide border border-white/10" data-testid="hero-badge">
              <MapPin className="w-3.5 h-3.5" /> Proudly serving Rangiora
            </span>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.02]" data-testid="hero-title">
              <span className="text-white">Clean concrete.</span><br />
              <span className="bg-gradient-to-r from-[#7DD3FC] via-[#0EA5E9] to-[#C0C5CC] bg-clip-text text-transparent">Bright pathways.</span><br />
              <span className="text-[#E5E7EB]">One trusted local crew.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#9CA3AF] max-w-xl leading-relaxed" data-testid="hero-subtitle">
              We pressure wash driveways and paths back to factory-fresh, then line them with continuous LED strips that make your place look incredible at night.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/free-quote" data-testid="hero-cta-quote" className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#0EA5E9] hover:bg-[#38BDF8] text-white font-medium px-6 py-3">
                Get a free quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/booking" data-testid="hero-cta-book" className="pill-btn inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/10 text-white font-medium px-6 py-3">
                Book a slot
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 reveal" style={{ animationDelay: "180ms" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(14,165,233,0.6)] ring-1 ring-white/10">
              <img src={IMAGES.hero} alt="Pristine home at dusk with LED pathway lighting" className="w-full h-[420px] sm:h-[520px] object-cover" data-testid="hero-image" />
              <div className="absolute bottom-4 left-4 right-4 sm:left-6 sm:right-6 bg-black/70 backdrop-blur-md rounded-2xl px-4 py-3 flex items-center justify-between border border-white/10">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#9CA3AF]">Pressure washing</div>
                  <div className="font-heading text-xl font-bold text-white">$3 / sqm</div>
                </div>
                <div className="h-10 w-px bg-white/10" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#9CA3AF]">LED lighting</div>
                  <div className="font-heading text-xl font-bold text-[#0EA5E9]">Quote on request</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES - LIGHT */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="grid sm:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={f.title} className="reveal ba-card rounded-2xl bg-[#F9F9FB] border border-gray-100 p-6" style={{ animationDelay: `${i * 80}ms` }} data-testid={`feature-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-white border border-gray-100 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#0EA5E9]" />
                </div>
                <div className="font-heading text-xl font-semibold text-[#111827]">{f.title}</div>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE - LIGHT */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20" data-testid="showcase-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#0EA5E9] font-semibold">Before / After</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#111827] mt-3">See the difference, sqm by sqm.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">Every driveway has a backstory. Moss, dirt, tyre marks. We bring concrete back to its natural finish without damaging the surface - then, if you like, we line your pathways with continuous LED strips for a warm, polished look at night.</p>
            <p className="mt-2 text-xs text-[#4B5563] italic">Drag the slider to reveal the result →</p>
            <div className="mt-6 flex gap-3">
              <Link to="/what-we-offer" data-testid="showcase-cta" className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#0A0A0B] hover:bg-black text-white font-medium px-5 py-2.5 text-sm">
                Explore our services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div>
            <BeforeAfterSlider
              beforeSrc={IMAGES.pw_before}
              afterSrc={IMAGES.pw_after}
              beforeAlt="Dirty concrete driveway before pressure washing"
              afterAlt="Clean concrete driveway after pressure washing"
              height="h-72 sm:h-96"
              testid="home-ba-slider"
            />
          </div>
        </div>
      </section>

      {/* TRUST - DARK */}
      <section className="bg-[#0A0A0B] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 left-1/3 w-[400px] h-[400px] rounded-full bg-[#0EA5E9]/15 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <h3 className="font-heading text-2xl sm:text-3xl font-bold">Ready when you are.</h3>
            <p className="mt-2 text-[#9CA3AF] max-w-2xl">Free quotes, fixed pricing on washing, and tidy job sites every time. Book a morning or afternoon slot - we handle the rest.</p>
          </div>
          <div className="flex md:justify-end gap-3">
            <Link to="/free-quote" data-testid="trust-quote" className="pill-btn inline-flex items-center gap-2 rounded-full bg-[#0EA5E9] hover:bg-[#38BDF8] text-white font-semibold px-5 py-2.5 text-sm">
              Free quote
            </Link>
            <Link to="/booking" data-testid="trust-book" className="pill-btn inline-flex items-center gap-2 rounded-full bg-white hover:bg-[#C0C5CC] text-[#0A0A0B] font-semibold px-5 py-2.5 text-sm">
              Book now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
